import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { PawPrint, Squiggle } from "./Doodles";

const PHONE_BOOTH_PHOTO =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_archie-builds/artifacts/1vdl1e7u_IMG_7839.jpeg";
const ARCHIE_PHOTO =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_archie-builds/artifacts/86mb4ver_IMG_7840.jpeg";

const Story = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const photoY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section id="story" ref={ref} data-testid="story-section" className="relative scroll-mt-24 border-y-2 border-[#16294B] bg-[#F4E9D2] py-24 md:py-32" aria-label="Why I started Project Archie">
      <div className="mx-auto grid max-w-6xl items-center gap-14 px-5 sm:px-8 md:grid-cols-2 md:gap-16">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-3">
            <Squiggle className="h-6 w-10" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#E63946]">The story</span>
          </div>
          <h2 className="mt-4 font-display text-5xl leading-tight text-[#16294B] md:text-6xl">
            Why I started <span className="text-[#E63946]">Project Archie</span>
          </h2>

          <div className="mt-8 space-y-6">
            {[
              <>
                Hey, I'm <strong className="text-[#16294B]">Imran</strong> — a medical student who has developed a real interest in surgery, especially <span className="hl-underline font-semibold text-[#16294B]">Orthopaedics</span>.
              </>,
              <>
                After attending a few hands-on events where I had to figure out drills and basic building tasks on the fly, I realized I could use more practice.
              </>,
              <>
                A few friends and I started thinking: what if we could build those hands-on skills while also giving back to the community? Named after my energetic Cavapoo, <strong className="text-[#16294B]">Archie</strong>, the idea became Project Archie.
              </>,
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="flex gap-4"
              >
                <PawPrint className="mt-1 h-6 w-6 shrink-0" color="#3B82F6" />
                <p className="text-base leading-relaxed text-[#4A5568] md:text-lg">{p}</p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-8 inline-block -rotate-1 rounded-lg border-2 border-[#16294B] bg-[#FBF3E3] px-5 py-3 font-display text-xl text-[#16294B] shadow-[4px_4px_0_0_#16294B]"
          >
            Hands-on learning + community impact
          </motion.p>
        </motion.div>

        <motion.div style={{ y: photoY }} className="relative flex items-start gap-4 sm:gap-6">
          <motion.figure
            initial={{ opacity: 0, rotate: -7, y: 40 }}
            whileInView={{ opacity: 1, rotate: -2, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-1/2 border-2 border-[#16294B] bg-[#FBF3E3] p-2.5 shadow-[8px_8px_0_0_#16294B]"
          >
            <img
              data-testid="story-photo-imran"
              src={PHONE_BOOTH_PHOTO}
              alt="Imran, co-founder of Project Archie, standing beside red telephone boxes"
              loading="lazy"
              className="h-auto w-full border-2 border-[#16294B]"
            />
            <figcaption className="px-1 pt-2 pb-1 text-center font-display text-lg text-[#16294B]">Imran, co-founder</figcaption>
          </motion.figure>

          <motion.figure
            initial={{ opacity: 0, rotate: 7, y: 40 }}
            whileInView={{ opacity: 1, rotate: 2, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="relative mt-12 w-1/2 border-2 border-[#16294B] bg-[#FBF3E3] p-2.5 shadow-[8px_8px_0_0_#E63946]"
          >
            <img
              data-testid="story-photo-archie"
              src={ARCHIE_PHOTO}
              alt="Imran and a friend holding Archie the Cavapoo, the dog who inspired Project Archie"
              loading="lazy"
              className="h-auto w-full border-2 border-[#16294B]"
            />
            <figcaption className="px-1 pt-2 pb-1 text-center font-display text-lg text-[#16294B]">...and Archie himself</figcaption>
            <span className="absolute -top-4 -right-3 rotate-12">
              <PawPrint className="h-10 w-10" color="#E63946" />
            </span>
          </motion.figure>
        </motion.div>
      </div>
    </section>
  );
};

export default Story;
